import { useState, type CSSProperties } from 'react';
import { ApercuPiece, type ApercuPieceProps } from '../../../../../packages/ui/src/composants/ApercuPiece.js';
import { styleBoutonPrincipal, styleBoutonSecondaire } from '../../../../../packages/ui/src/composants/styles.js';
import { formaterTaille } from '../../../../../packages/ui/src/format.js';
import { COULEURS, ESPACEMENTS, styleBase, styleParagraphe } from '../../../../../packages/ui/src/tokens/tokens.js';
import { ficheTitre, separerDepots, typeDePiece, videosDuDossier } from './depots.js';
import { filtrerDocuments, optionsFiltres, type FiltreDocuments } from './filtres.js';
import { ETATS_ECRANS, LIBELLES_CATEGORIES, LIBELLES_SOURCES, LIBELLES_STATUTS, MOTIFS_REFUS_PIECE, REPLI_DEMANDE_PHOTO } from './libelles.js';
import { avisNettete, messageMeilleurePhoto, type DecisionExamen } from './revue.js';
import type { DocumentLakou, VersionDocument } from './types.js';
import { margeDefilement, transformation, type EtatVisionneuse } from './visionneuse.js';

export interface PropsEcranDocuments {
  /** Titre de l'écran (celui du contrat quand l'écran est branché dans la coquille). */
  titreEcran?: string;
  documents: readonly DocumentLakou[];
  filtre: FiltreDocuments;
  /** Noms lisibles des personnes et des biens (jamais un identifiant à l'écran). */
  libellesPersonnes: Readonly<Record<string, string>>;
  libellesBiens: Readonly<Record<string, string>>;
  urlContenu: (document: DocumentLakou) => string;
  selection: string | null;
  visionneuse: EtatVisionneuse;
  versions: readonly VersionDocument[];
  onFiltre?: (filtre: FiltreDocuments) => void;
  onSelectionner?: (id: string) => void;
  onZoom?: (sens: 'plus' | 'moins') => void;
  onPivoter?: (sens: 'horaire' | 'antihoraire') => void;
  onPage?: (page: number) => void;
  /** `message` : texte envoyé à l'héritier, relu et éventuellement modifié par l'équipe (décision `meilleure_photo`). */
  onExaminer?: (decision: DecisionExamen, documentId: string, message?: string) => void;
  /** À qui écrire et vers quelle page (M05) pour une meilleure photo ; à défaut, le message reste à compléter par l'équipe. */
  contexteDemandePhoto?: (document: DocumentLakou) => { prenomHeritier: string; lien: string };
}

const VARIANTE = 'equipe' as const;
const rien = (): void => undefined;
const titreSection: CSSProperties = { ...styleBase(VARIANTE), fontSize: 20, margin: 0 };
const listeNue: CSSProperties = { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: ESPACEMENTS.s };

function proprietesApercu(document: DocumentLakou, urlContenu: (d: DocumentLakou) => string): ApercuPieceProps | null {
  const fiche = ficheTitre(document, urlContenu);
  if (fiche) return { type: fiche.type, src: fiche.src, titre: fiche.titre, variante: VARIANTE };
  const type = typeDePiece(document.currentVersion.mime);
  if (type === 'autre') return null;
  const { durationS } = document.currentVersion.preview;
  return {
    type,
    src: urlContenu(document),
    titre: document.title,
    variante: VARIANTE,
    ...(type === 'video' && durationS !== null ? { dureeSecondes: durationS } : {}),
  };
}

/** Nombre de pages d'un PDF, pour la pagination affichée HORS du bloc tourné. */
const pagesDuPdf = (document: DocumentLakou): number | null =>
  typeDePiece(document.currentVersion.mime) === 'pdf' ? document.currentVersion.preview.pages : null;

function Miniature({ document }: { document: DocumentLakou }) {
  const { thumbnailUrl } = document.currentVersion.preview;
  const cadre: CSSProperties = {
    width: 56,
    height: 56,
    borderRadius: 6,
    border: `1px solid ${COULEURS.bordure}`,
    objectFit: 'cover',
    flexShrink: 0,
  };
  if (thumbnailUrl) return <img src={thumbnailUrl} alt={`Miniature : ${document.title}`} style={cadre} />;
  const nom = { photo: 'Photo', video: 'Vidéo', pdf: 'PDF', autre: 'Fichier' }[typeDePiece(document.currentVersion.mime)];
  return (
    <span
      aria-hidden="true"
      style={{
        ...cadre,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: COULEURS.fondDoux,
        fontSize: 12,
      }}
    >
      {nom}
    </span>
  );
}

function Ligne({ document, choisi, onSelectionner }: { document: DocumentLakou; choisi: boolean; onSelectionner: (id: string) => void }) {
  return (
    <li>
      <button
        type="button"
        aria-pressed={choisi}
        onClick={() => onSelectionner(document.id)}
        style={{
          ...styleBoutonSecondaire(VARIANTE),
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: ESPACEMENTS.s,
          textAlign: 'left',
          borderColor: choisi ? COULEURS.bouton : COULEURS.marine,
        }}
      >
        <Miniature document={document} />
        <span style={{ display: 'flex', flexDirection: 'column' }}>
          <span>{document.title}</span>
          <span style={{ color: COULEURS.texteSecondaire, fontSize: 14 }}>
            {`${LIBELLES_CATEGORIES.valeurs[document.category]} · `}
            <span data-statut={document.status} style={{ border: `1px solid ${COULEURS.bordure}`, borderRadius: 4, padding: '0 6px' }}>
              {LIBELLES_STATUTS.valeurs[document.status]}
            </span>
          </span>
        </span>
      </button>
    </li>
  );
}

function Liste({
  titre,
  documents,
  selection,
  onSelectionner,
  pied,
}: {
  titre: string;
  documents: readonly DocumentLakou[];
  selection: string | null;
  onSelectionner: (id: string) => void;
  pied?: string;
}) {
  if (documents.length === 0) return null;
  return (
    <section aria-label={titre} style={{ display: 'flex', flexDirection: 'column', gap: ESPACEMENTS.s }}>
      <h2 style={titreSection}>{titre}</h2>
      {pied ? <p style={styleParagraphe(VARIANTE)}>{pied}</p> : null}
      <ul style={listeNue}>
        {documents.map((d) => (
          <Ligne key={d.id} document={d} choisi={d.id === selection} onSelectionner={onSelectionner} />
        ))}
      </ul>
    </section>
  );
}

function Filtre({
  nom,
  valeur,
  options,
  libelles,
  vide,
  repli,
  onChange,
}: {
  nom: string;
  valeur: string | undefined;
  options: readonly string[];
  libelles: Readonly<Record<string, string>>;
  vide: string;
  repli: string;
  onChange: (v: string | undefined) => void;
}) {
  return (
    <label style={{ ...styleBase(VARIANTE), display: 'flex', flexDirection: 'column', gap: ESPACEMENTS.xs }}>
      {nom}
      <select
        value={valeur ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value)}
        style={{ ...styleBase(VARIANTE), minHeight: 44 }}
      >
        <option value="">{vide}</option>
        {options.map((id) => (
          <option key={id} value={id}>
            {libelles[id] ?? repli}
          </option>
        ))}
      </select>
    </label>
  );
}

function PanneauApercu(props: PropsEcranDocuments & { document: DocumentLakou }) {
  const {
    document,
    visionneuse,
    versions,
    urlContenu,
    contexteDemandePhoto,
    onZoom = rien,
    onPivoter = rien,
    onPage = rien,
    onExaminer = rien,
  } = props;
  const [message, setMessage] = useState<string | null>(null);
  const [refus, setRefus] = useState<{ code: string; precision: string; erreur: boolean } | null>(null);
  const apercu = proprietesApercu(document, urlContenu);
  const derniere = versions.reduce((max, v) => Math.max(max, v.versionNo), 0);
  const type = typeDePiece(document.currentVersion.mime);
  const totalPages = pagesDuPdf(document);

  const ouvrirDemande = (): void => {
    const { prenomHeritier, lien } = contexteDemandePhoto?.(document) ?? REPLI_DEMANDE_PHOTO;
    // La pièce est désignée par sa catégorie, jamais par son titre : il peut nommer le mandant.
    const libellePiece = `pièce (${LIBELLES_CATEGORIES.valeurs[document.category].toLowerCase()})`;
    setMessage(messageMeilleurePhoto({ prenomHeritier, libellePiece, lien }));
  };

  const confirmerRefus = (): void => {
    if (refus === null) return;
    const libre = refus.code === MOTIFS_REFUS_PIECE.codeAutre;
    const motif = libre ? refus.precision.trim() : refus.code;
    if (motif === '') {
      setRefus({ ...refus, erreur: true });
      return;
    }
    onExaminer('refuser', document.id, motif);
    setRefus(null);
  };

  return (
    <section aria-label={`Aperçu : ${document.title}`} style={{ display: 'flex', flexDirection: 'column', gap: ESPACEMENTS.s }}>
      <h2 style={titreSection}>Aperçu</h2>
      {apercu ? (
        <div data-defilement="" style={{ overflow: 'auto', maxWidth: '100%', padding: `${margeDefilement(visionneuse)}%` }}>
          <div data-media-transforme="" style={{ transform: transformation(visionneuse), transformOrigin: 'center', width: 'fit-content' }}>
            <ApercuPiece {...apercu} page={visionneuse.page} onPageChange={onPage} />
          </div>
        </div>
      ) : (
        <p style={styleParagraphe(VARIANTE)}>Aperçu indisponible pour ce type de fichier.</p>
      )}
      {type === 'pdf' && totalPages !== null ? (
        <div role="group" aria-label="Pages" style={{ display: 'flex', alignItems: 'center', gap: ESPACEMENTS.s, flexWrap: 'wrap' }}>
          <button
            type="button"
            disabled={visionneuse.page <= 1}
            onClick={() => onPage(visionneuse.page - 1)}
            style={styleBoutonSecondaire(VARIANTE)}
          >
            Page précédente
          </button>
          <p aria-live="polite" style={{ ...styleParagraphe(VARIANTE), fontWeight: 600 }}>{`Page ${visionneuse.page} / ${totalPages}`}</p>
          <button
            type="button"
            disabled={visionneuse.page >= totalPages}
            onClick={() => onPage(visionneuse.page + 1)}
            style={styleBoutonSecondaire(VARIANTE)}
          >
            Page suivante
          </button>
        </div>
      ) : null}
      {type === 'photo' ? (
        <p
          role="note"
          style={{ ...styleParagraphe(VARIANTE), backgroundColor: COULEURS.fondDoux, borderRadius: 8, padding: ESPACEMENTS.s }}
        >
          {avisNettete(document.currentVersion.quality)}
        </p>
      ) : null}
      {type === 'photo' || type === 'pdf' ? (
        <div
          role="group"
          aria-label="Zoom et rotation"
          style={{ display: 'flex', flexWrap: 'wrap', gap: ESPACEMENTS.s, alignItems: 'center' }}
        >
          <button type="button" onClick={() => onZoom('plus')} style={styleBoutonSecondaire(VARIANTE)}>
            Agrandir
          </button>
          <button type="button" onClick={() => onZoom('moins')} style={styleBoutonSecondaire(VARIANTE)}>
            Réduire
          </button>
          <span aria-live="polite" style={styleParagraphe(VARIANTE)}>{`${Math.round(visionneuse.zoom * 100)} %`}</span>
          <button type="button" onClick={() => onPivoter('antihoraire')} style={styleBoutonSecondaire(VARIANTE)}>
            Pivoter à gauche
          </button>
          <button type="button" onClick={() => onPivoter('horaire')} style={styleBoutonSecondaire(VARIANTE)}>
            Pivoter à droite
          </button>
        </div>
      ) : null}
      <div role="group" aria-label="Décision sur la pièce" style={{ display: 'flex', flexWrap: 'wrap', gap: ESPACEMENTS.s }}>
        <button type="button" onClick={() => onExaminer('valider', document.id)} style={styleBoutonPrincipal(VARIANTE)}>
          Valider
        </button>
        <button type="button" onClick={ouvrirDemande} style={styleBoutonSecondaire(VARIANTE)}>
          Demander une meilleure photo
        </button>
        <button type="button" onClick={() => setRefus({ code: '', precision: '', erreur: false })} style={styleBoutonSecondaire(VARIANTE)}>
          Refuser
        </button>
      </div>
      {refus !== null ? (
        <section aria-label="Refus de la pièce" style={{ display: 'flex', flexDirection: 'column', gap: ESPACEMENTS.s }}>
          <label style={{ ...styleBase(VARIANTE), display: 'flex', flexDirection: 'column', gap: ESPACEMENTS.xs }}>
            Motif du refus
            <select
              value={refus.code}
              required
              onChange={(e) => setRefus({ ...refus, code: e.target.value, erreur: false })}
              style={{ ...styleBase(VARIANTE), boxSizing: 'border-box', width: '100%' }}
            >
              <option value="">Choisissez un motif</option>
              {MOTIFS_REFUS_PIECE.valeurs.map((m) => (
                <option key={m.code} value={m.code}>
                  {m.libelle}
                </option>
              ))}
            </select>
          </label>
          {refus.code === MOTIFS_REFUS_PIECE.codeAutre ? (
            <label style={{ ...styleBase(VARIANTE), display: 'flex', flexDirection: 'column', gap: ESPACEMENTS.xs }}>
              Précisez le motif
              <textarea
                value={refus.precision}
                rows={3}
                required
                onChange={(e) => setRefus({ ...refus, precision: e.target.value, erreur: false })}
                style={{ ...styleBase(VARIANTE), boxSizing: 'border-box', width: '100%' }}
              />
            </label>
          ) : null}
          {refus.erreur ? (
            <p role="alert" style={{ ...styleParagraphe(VARIANTE), color: COULEURS.erreur }}>
              {ETATS_ECRANS.d09.motifRequis}
            </p>
          ) : null}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: ESPACEMENTS.s }}>
            <button type="button" onClick={confirmerRefus} style={styleBoutonPrincipal(VARIANTE)}>
              Confirmer le refus
            </button>
            <button type="button" onClick={() => setRefus(null)} style={styleBoutonSecondaire(VARIANTE)}>
              Annuler
            </button>
          </div>
        </section>
      ) : null}
      {message !== null ? (
        <section aria-label="Demande de meilleure photo" style={{ display: 'flex', flexDirection: 'column', gap: ESPACEMENTS.s }}>
          <label style={{ ...styleBase(VARIANTE), display: 'flex', flexDirection: 'column', gap: ESPACEMENTS.xs }}>
            Message à l’héritier
            <textarea
              value={message}
              rows={9}
              onChange={(e) => setMessage(e.target.value)}
              style={{ ...styleBase(VARIANTE), boxSizing: 'border-box', width: '100%', minHeight: 160 }}
            />
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: ESPACEMENTS.s }}>
            <button
              type="button"
              onClick={() => {
                onExaminer('meilleure_photo', document.id, message);
                setMessage(null);
              }}
              style={styleBoutonPrincipal(VARIANTE)}
            >
              Envoyer la demande
            </button>
            <button type="button" onClick={() => setMessage(null)} style={styleBoutonSecondaire(VARIANTE)}>
              Annuler
            </button>
          </div>
        </section>
      ) : null}
      {versions.length > 0 ? (
        <section aria-label="Versions" style={{ display: 'flex', flexDirection: 'column', gap: ESPACEMENTS.xs }}>
          <h3 style={{ ...styleBase(VARIANTE), fontSize: 16, margin: 0 }}>Versions</h3>
          <ul style={listeNue}>
            {[...versions]
              .sort((a, b) => b.versionNo - a.versionNo)
              .map((v) => (
                <li key={v.versionNo} style={styleParagraphe(VARIANTE)}>
                  {`Version ${v.versionNo}${v.versionNo === derniere ? ' (actuelle)' : ''} · ${LIBELLES_SOURCES.valeurs[v.source]} · ${formaterTaille(v.size)}`}
                </li>
              ))}
          </ul>
        </section>
      ) : null}
    </section>
  );
}

/** D09 : les documents du dossier (équipe MAAT). Filtres par personne et par bien, miniatures, aperçu, versions, vidéos. */
export function EcranDocuments(props: PropsEcranDocuments) {
  const {
    titreEcran = 'Documents',
    documents,
    filtre,
    libellesPersonnes,
    libellesBiens,
    selection,
    onFiltre = rien,
    onSelectionner = rien,
  } = props;
  const options = optionsFiltres(documents);
  const visibles = filtrerDocuments(documents, filtre);
  const { actesNotaries, piecesFamilles } = separerDepots(visibles);
  const videos = videosDuDossier(visibles);
  const famillesSansVideo = piecesFamilles.filter((d) => !videos.includes(d));
  const actesSansVideo = actesNotaries.filter((d) => !videos.includes(d));
  const courant = selection === null ? undefined : visibles.find((d) => d.id === selection);

  return (
    <section style={{ ...styleBase(VARIANTE), display: 'flex', flexDirection: 'column', gap: ESPACEMENTS.m, padding: ESPACEMENTS.m }}>
      <h1 style={{ ...styleBase(VARIANTE), fontSize: 28, margin: 0 }}>{titreEcran}</h1>
      <div role="group" aria-label="Filtres" style={{ display: 'flex', flexWrap: 'wrap', gap: ESPACEMENTS.m }}>
        <Filtre
          nom="Personne"
          valeur={filtre.personneId}
          options={options.personnes}
          libelles={libellesPersonnes}
          vide="Toutes les personnes"
          repli="Personne du dossier"
          onChange={(personneId) => onFiltre({ ...filtre, personneId })}
        />
        <Filtre
          nom="Bien"
          valeur={filtre.bienId}
          options={options.biens}
          libelles={libellesBiens}
          vide="Tous les biens"
          repli="Bien du dossier"
          onChange={(bienId) => onFiltre({ ...filtre, bienId })}
        />
      </div>
      {visibles.length === 0 ? (
        <p role="status" style={styleParagraphe(VARIANTE)}>
          {filtre.personneId === undefined && filtre.bienId === undefined ? ETATS_ECRANS.d09.aucunePiece : ETATS_ECRANS.d09.aucunResultat}
        </p>
      ) : null}
      <Liste
        titre="Actes notariés"
        documents={actesSansVideo}
        selection={selection}
        onSelectionner={onSelectionner}
        pied="Actes déjà obtenus auprès du notaire, distincts des pièces envoyées par les familles."
      />
      <Liste titre="Pièces des familles" documents={famillesSansVideo} selection={selection} onSelectionner={onSelectionner} />
      <Liste
        titre="Vidéos du dossier"
        documents={videos}
        selection={selection}
        onSelectionner={onSelectionner}
        pied={`${videos.length} ${videos.length > 1 ? 'vidéos' : 'vidéo'}`}
      />
      {courant ? <PanneauApercu key={courant.id} {...props} document={courant} /> : null}
    </section>
  );
}
